import * as React from 'react';
import { createUseStyles } from 'react-jss';
import { Theme } from 'helpers/theme';
import Button from 'components/Button/Button';
import { createFragmentContainer, graphql } from 'react-relay';
import { BackgroundCheckCard_delegate } from './__generated__/BackgroundCheckCard_delegate.graphql';
import { BackgroundCheckCard_manager } from './__generated__/BackgroundCheckCard_manager.graphql';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faIdBadge,
  faRotateRight,
  faShieldCheck as duoShield,
  faSpinner,
  faUserCheck,
  faClipboardCheck
} from '@fortawesome/pro-duotone-svg-icons';
import { useActionMenuDispatch } from 'components/ActionMenu/ActionMenuProvider';
import RequestBackgroundCheckModal, {
  CheckType
} from 'features/background-checks/components/RequestBackgroundCheckModal/RequestBackgroundCheckModal';
import CheckStatus from 'features/background-checks/components/CheckStatus';
import {
  getStatus,
  statusText
} from 'features/background-checks/utils/helpers';
import DataItem from './DataItem';
import moment, { Moment } from 'moment';
import {
  faShieldCheck,
  faShieldExclamation,
  faShieldHalved
} from '@fortawesome/pro-solid-svg-icons';
import AutomatedCheckModal from 'features/background-checks/components/AutomatedCheckModal/AutomatedCheckModal';

const useStyles = createUseStyles((theme: Theme) => ({
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '1rem'
  },
  subtitle: {
    fontSize: '0.9rem',
    fontWeight: 600,
    margin: 0
  },
  noBackgroundCheck: {
    color: '#6A728F',
    fontSize: '0.8rem'
  },
  backgroundCheckPending: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: '2rem'
  },
  pending: {
    color: '#69728f',
    fontSize: '0.8rem'
  },
  bgItems: {
    display: 'flex',
    gap: '10px',
    marginTop: 10,
    flexWrap: 'wrap'
  },
  buttons: {
    display: 'flex',
    gap: '10px',
    flexWrap: 'wrap',
    justifyContent: 'center'
  },
  emptyPanel: {
    marginTop: 12,
    padding: '26px 20px',
    background: '#f8f9fb',
    border: '1px solid #ededed',
    borderRadius: 8,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    gap: 12
  },
  emptyShield: {
    fontSize: 28,
    color: '#6A728F'
  }
}));

type Props = {
  delegate: BackgroundCheckCard_delegate;
  manager: BackgroundCheckCard_manager;
  onRequestBackgroundCheck(
    includesDBS: boolean,
    checkType: CheckType,
    idExpiry?: Moment
  ): void;
};

function BackgroundCheckCard({
  delegate,
  manager,
  onRequestBackgroundCheck
}: Props) {
  const classes = useStyles();
  const action = useActionMenuDispatch();

  const [bgOpen, setBgOpen] = React.useState(false);
  const onRequest = () => setBgOpen(true);

  const requestModal = (
    <RequestBackgroundCheckModal
      isOpen={bgOpen}
      renewalCost={manager.company.renewalPennyPrice}
      managerName={`${manager.firstName} ${manager.lastName}`}
      companyName={manager.company.name}
      delegate={{
        uuid: delegate.uuid,
        firstName: delegate.firstName,
        lastName: delegate.lastName,
        email: delegate.email,
        profileImageUrl: delegate.profileImageUrl,
        hasActiveBackgroundCheck: delegate.hasActiveBackgroundCheck
      }}
      onRequest={(
        _delegateUUID: string,
        checked: boolean,
        checkType: CheckType,
        idDate?: Moment
      ) => {
        onRequestBackgroundCheck(checked, checkType, idDate);
        setBgOpen(false);
      }}
      onClose={() => setBgOpen(false)}
      checkCost={manager.company.backgroundCheckPennyPrice}
      dbsCost={manager.company.dbsPennyPrice}
      dbsOnlyCost={manager.company.dbsOnlyPennyPrice}
      checksLeft={
        (manager.company?.invoicing?.backgroundChecksPrePurchased ?? 0) -
        (manager.company.invoicing?.backgroundChecksUsed ?? 0)
      }
      dbsLeft={
        (manager.company.invoicing?.dbsPrePurchased ?? 0) -
        (manager.company.invoicing?.dbsUsed ?? 0)
      }
      renewalsLeft={
        (manager.company.invoicing?.renewalsPrePurchased ?? 0) -
        (manager.company.invoicing?.renewalsUsed ?? 0)
      }
    />
  );

  const onUploadExisting = (evt: any) => {
    action({
      type: 'show',
      children: (
        <AutomatedCheckModal
          userName="test"
          delegateUUID={delegate.uuid}
          onClose={() => {
            action({ type: 'hide' });
            window.location.reload();
          }}
          successPennyPrice={manager.company.verificationSuccessPennyPrice}
          failurePennyPrice={manager.company.verificationFailurePennyPrice}
        />
      ),
      position: {
        x: evt.clientX,
        y: evt.clientY,
        placement: 'modal'
      }
    });
  };

  return (
    <div>
      {requestModal}
      <div>
        <div className={classes.header}>
          <p className={classes.subtitle}>
            Background check{' '}
            {delegate.pendingBackgroundCheck && (
              <span className={classes.pending}>(pending)</span>
            )}
          </p>
          {delegate.currentBackgroundCheck && !delegate.pendingBackgroundCheck && (
            <Button archetype="flat" onClick={onRequest}>
              <FontAwesomeIcon icon={faRotateRight} /> Request renewal
            </Button>
          )}
        </div>
        {!delegate.hasActiveBackgroundCheck &&
          !delegate.backgroundCheckPending && (
            <div className={classes.emptyPanel}>
              <FontAwesomeIcon
                icon={faShieldHalved}
                className={classes.emptyShield}
              />
              <p className={classes.noBackgroundCheck}>
                No TTC background check present
              </p>
              <div className={classes.buttons}>
                <Button archetype="flat" onClick={onUploadExisting}>
                  <FontAwesomeIcon icon={faClipboardCheck} color="#0957ff" />{' '}
                  Upload existing check
                </Button>
                <Button archetype="flatPrimary" onClick={onRequest}>
                  <FontAwesomeIcon icon={duoShield} /> Request background check
                </Button>
              </div>
            </div>
          )}
        {delegate.pendingBackgroundCheck &&
          !delegate.pendingBackgroundCheck.isRenewal &&
          !delegate.pendingBackgroundCheck.onlyDBS && (
            <div className={classes.backgroundCheckPending}>
              <CheckStatus
                status={getStatus(delegate.pendingBackgroundCheck)}
              />
              <p className={classes.noBackgroundCheck}>
                {statusText[getStatus(delegate.pendingBackgroundCheck)]}
              </p>
            </div>
          )}

        {delegate.currentBackgroundCheck && (
          <>
            <div className={classes.bgItems}>
              {delegate.currentBackgroundCheck.onlyDBS ? (
                <DataItem
                  label={'No background check'}
                  icon={
                    delegate.currentBackgroundCheck.dbsExpiryDate &&
                    moment(delegate.currentBackgroundCheck.dbsExpiryDate).diff(
                      moment(),
                      'days'
                    ) > 60
                      ? faShieldHalved
                      : faShieldExclamation
                  }
                  iconColor={'#f68f1f'}
                  helpText={
                    delegate.currentBackgroundCheck.dbsExpiryDate
                      ? 'DBS expiration date'
                      : 'ID or DBS date is not valid'
                  }
                >
                  {delegate.currentBackgroundCheck.dbsExpiryDate
                    ? `DBS Only - Expires ${moment(
                        delegate.currentBackgroundCheck.dbsExpiryDate
                      ).format('DD/MM/YYYY')}`
                    : 'DBS invalid'}
                </DataItem>
              ) : (
                <DataItem
                  label={
                    delegate.currentBackgroundCheck.expiresAt
                      ? moment(
                          delegate.currentBackgroundCheck.expiresAt
                        ).fromNow()
                      : 'ID or DBS not valid'
                  }
                  icon={
                    delegate.currentBackgroundCheck.expiresAt &&
                    moment(delegate.currentBackgroundCheck.expiresAt).diff(
                      moment(),
                      'days'
                    ) > 60
                      ? faShieldCheck
                      : faShieldExclamation
                  }
                  iconColor={
                    delegate.currentBackgroundCheck.expiresAt &&
                    moment(delegate.currentBackgroundCheck.expiresAt).diff(
                      moment(),
                      'days'
                    ) > 60
                      ? '#1FD426'
                      : '#f68f1f'
                  }
                  helpText={
                    delegate.currentBackgroundCheck.expiresAt
                      ? 'Background check expiration date'
                      : 'ID or DBS date is not valid'
                  }
                >
                  {delegate.currentBackgroundCheck.expiresAt
                    ? `Expires ${moment(
                        delegate.currentBackgroundCheck.expiresAt
                      ).format('DD/MM/YYYY')} ${
                        !delegate.currentBackgroundCheck.isCAA &&
                        !delegate.currentBackgroundCheck.isVerification
                          ? '(Non-CAA)'
                          : ''
                      }`
                    : 'Background check invalid'}
                </DataItem>
              )}
              <DataItem
                label={
                  delegate.currentBackgroundCheck.idExpiryDate
                    ? `Expires ${moment(
                        delegate.currentBackgroundCheck.idExpiryDate
                      ).format('DD/MM/YYYY')}`
                    : 'Not present'
                }
                icon={
                  delegate.currentBackgroundCheck.idExpiryDate
                    ? faUserCheck
                    : faShieldExclamation
                }
                iconColor={
                  delegate.currentBackgroundCheck.idExpiryDate &&
                  moment(delegate.currentBackgroundCheck.idExpiryDate).diff(
                    moment(),
                    'days'
                  ) > 60
                    ? '#6A728F'
                    : '#f68f1f'
                }
                helpText="ID expiration date"
                style={{ width: 'calc(50% - 5px)' }}
              >
                ID
              </DataItem>
              <DataItem
                label={
                  delegate.currentBackgroundCheck.dbsExpiryDate
                    ? `Expires ${moment(
                        delegate.currentBackgroundCheck.dbsExpiryDate
                      ).format('DD/MM/YYYY')}`
                    : 'Not present'
                }
                icon={
                  delegate.currentBackgroundCheck.dbsExpiryDate
                    ? faIdBadge
                    : faShieldExclamation
                }
                iconColor={
                  delegate.currentBackgroundCheck.dbsExpiryDate &&
                  moment(delegate.currentBackgroundCheck.dbsExpiryDate).diff(
                    moment(),
                    'days'
                  ) > 60
                    ? '#6A728F'
                    : '#f68f1f'
                }
                helpText="DBS expiration date"
                style={{ width: 'calc(50% - 5px)' }}
              >
                DBS
              </DataItem>
            </div>
          </>
        )}
        {delegate.pendingBackgroundCheck &&
          delegate.pendingBackgroundCheck.isRenewal && (
            <DataItem
              label="Background check renewal requested"
              icon={faSpinner}
              iconColor="#0b57ff"
              helpText="A renewal has been requested, and is being carried out"
              style={{ marginTop: 10 }}
            >
              Renewal pending
            </DataItem>
          )}
        {delegate.pendingBackgroundCheck &&
          delegate.pendingBackgroundCheck.onlyDBS && (
            <DataItem
              label="A DBS check has been requested"
              icon={faSpinner}
              iconColor="#0b57ff"
              helpText="A DBS has been requested, and is being carried out"
              style={{ marginTop: 10 }}
            >
              DBS pending
            </DataItem>
          )}
      </div>
    </div>
  );
}

const BackgroundCheckCardFrag = createFragmentContainer(BackgroundCheckCard, {
  delegate: graphql`
    fragment BackgroundCheckCard_delegate on Delegate {
      uuid
      firstName
      lastName
      email
      profileImageUrl
      backgroundCheckPending
      hasActiveBackgroundCheck
      pendingBackgroundCheck {
        approvalDate
        applicationSentAt
        applicationReceivedAt
        hasAllResponses
        requestedDBS
        isRenewal
        isVerification
        idExpiryDate
        dbsExpiryDate
        onlyDBS
      }
      currentBackgroundCheck {
        approvalDate
        expiresAt
        idExpiryDate
        dbsExpiryDate
        isVerification
        isCAA
        onlyDBS
      }
    }
  `,
  manager: graphql`
    fragment BackgroundCheckCard_manager on Manager {
      firstName
      lastName
      company {
        backgroundCheckPennyPrice
        dbsPennyPrice
        dbsOnlyPennyPrice
        renewalPennyPrice
        name
        verificationSuccessPennyPrice
        verificationFailurePennyPrice
        invoicing {
          dbsUsed
          dbsPrePurchased
          backgroundChecksUsed
          backgroundChecksPrePurchased
          renewalsUsed
          renewalsPrePurchased
        }
      }
    }
  `
});

export default BackgroundCheckCardFrag;
